/**
 * Alerts Dashboard - View and Manage Declaration Expiry Alerts
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert as MuiAlert,
  Chip,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';

import { getAlerts, acknowledgeAlert, sendRenewalEmail, Alert } from '../../../services/supplierDeclarationService';

const AlertsDashboard: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acknowledging, setAcknowledging] = useState<string | null>(null);
  const [sendingRenewal, setSendingRenewal] = useState<string | null>(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [acknowledgedFilter, setAcknowledgedFilter] = useState<string>('');
  
  // Pagination
  const [skip, setSkip] = useState(0);
  const [limit] = useState(50);

  useEffect(() => {
    loadAlerts();
  }, [statusFilter, acknowledgedFilter, skip]);

  const loadAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const acknowledgedValue = acknowledgedFilter === '' 
        ? undefined 
        : acknowledgedFilter === 'true';
      
      const data = await getAlerts(
        statusFilter || undefined,
        acknowledgedValue,
        skip,
        limit
      );
      setAlerts(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load alerts');
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleSendRenewal = async (declarationId: string, alertId: string) => {
    setSendingRenewal(alertId);
    try {
      await sendRenewalEmail(declarationId);
      toast.success('Renewal email sent to supplier');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to send renewal email');
    } finally {
      setSendingRenewal(null);
    }
  };

  const handleAcknowledge = async (alertId: string) => {
    setAcknowledging(alertId);
    try {
      await acknowledgeAlert(alertId);
      toast.success('Alert acknowledged');
      loadAlerts(); // Refresh the list
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Failed to acknowledge alert');
    } finally {
      setAcknowledging(null);
    }
  };

  const getUrgencyColor = (daysBeforeExpiry?: number): 'error' | 'warning' | 'success' => {
    if (daysBeforeExpiry === undefined || daysBeforeExpiry === null) {
      return 'success';
    }
    if (daysBeforeExpiry < 7) {
      return 'error';
    }
    if (daysBeforeExpiry < 30) {
      return 'warning';
    }
    return 'success';
  };

  const getUrgencyIcon = (daysBeforeExpiry?: number) => {
    const color = getUrgencyColor(daysBeforeExpiry);
    if (color === 'error') {
      return <ErrorIcon color="error" />;
    }
    if (color === 'warning') {
      return <WarningIcon color="warning" />;
    }
    return <CheckIcon color="success" />;
  };

  const getDaysUntilExpiry = (validUntil?: string): number | null => {
    if (!validUntil) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const expiry = new Date(validUntil);
    expiry.setHours(0, 0, 0, 0);
    return Math.floor((expiry.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setAcknowledgedFilter('');
    setSkip(0);
  };

  const handleLoadMore = () => {
    setSkip(skip + limit);
  };

  const handleLoadPrevious = () => {
    setSkip(Math.max(0, skip - limit));
  };

  if (loading && alerts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Declaration Expiry Alerts
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setSkip(0);
                }}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="unsent">Unsent</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Acknowledged</InputLabel>
              <Select
                value={acknowledgedFilter}
                onChange={(e) => {
                  setAcknowledgedFilter(e.target.value);
                  setSkip(0);
                }}
                label="Acknowledged"
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Acknowledged</MenuItem>
                <MenuItem value="false">Unacknowledged</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleClearFilters}
              disabled={!statusFilter && !acknowledgedFilter}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && <MuiAlert severity="error" sx={{ mb: 2 }}>{error}</MuiAlert>}

      {alerts.length === 0 && !loading ? (
        <MuiAlert severity="info">No alerts found</MuiAlert>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell width={60}>Urgency</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Expiry Date</TableCell>
                  <TableCell>Days Until Expiry</TableCell>
                  <TableCell>Alert Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {alerts.map((alert) => (
                  <TableRow 
                    key={alert.id}
                    sx={{
                      bgcolor: alert.acknowledged ? 'inherit' : 'action.hover',
                    }}
                  >
                    <TableCell>
                      {getUrgencyIcon(alert.days_before_expiry)}
                    </TableCell>
                    <TableCell>{alert.supplier_name}</TableCell>
                    <TableCell>
                      {alert.product_description.substring(0, 50)}
                      {alert.product_description.length > 50 ? '...' : ''}
                    </TableCell>
                    <TableCell>{formatDate(alert.valid_until)}</TableCell>
                    <TableCell>
                      {(() => {
                        const days = getDaysUntilExpiry(alert.valid_until);
                        return (
                          <Chip
                            label={days !== null ? `${days} days` : 'N/A'}
                            color={getUrgencyColor(days ?? undefined)}
                            size="small"
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={alert.alert_type}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {alert.acknowledged ? (
                        <Chip
                          label="Acknowledged"
                          color="success"
                          size="small"
                          icon={<CheckIcon />}
                        />
                      ) : (
                        <Chip
                          label="Pending"
                          color="warning"
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                        {alert.declaration_status === 'active' ? (
                          <Chip label="Renewed" color="success" size="small" icon={<CheckIcon />} />
                        ) : (
                          <Button
                            size="small"
                            variant="contained"
                            color="warning"
                            onClick={() => handleSendRenewal(alert.declaration_id, alert.id)}
                            disabled={sendingRenewal === alert.id}
                          >
                            {sendingRenewal === alert.id ? 'Sending...' : 'Send Renewal Email'}
                          </Button>
                        )}
                        {!alert.acknowledged && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleAcknowledge(alert.id)}
                            disabled={acknowledging === alert.id}
                          >
                            {acknowledging === alert.id ? 'Acknowledging...' : 'Acknowledge'}
                          </Button>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination Controls */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={handleLoadPrevious}
              disabled={skip === 0 || loading}
            >
              Previous
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ alignSelf: 'center' }}>
              Showing {skip + 1} - {skip + alerts.length}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleLoadMore}
              disabled={alerts.length < limit || loading}
            >
              Next
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AlertsDashboard;
